/**
 * Subject Edit Form
 *
 * Mirrors SubjectForm shape but loads + PATCHes an existing Subject by id.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link as MuiLink,
  Stack,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Subject, SubjectUpdate } from '../../types/subject';

const SubjectEditForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [subject, setSubject] = useState<Subject | null>(null);
  const [formData, setFormData] = useState<SubjectUpdate>({});
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get<Subject>(`/api/subjects/${id}/`);
        setSubject(res.data);
        setFormData({ name: res.data.name, short_name: res.data.short_name });
      } catch (err) {
        console.error('Error loading subject:', err);
        setError('Failed to load subject.');
      } finally {
        setLoadingData(false);
      }
    };
    if (id) load();
  }, [id]);

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};
    if (formData.name !== undefined && formData.name.trim() === '') {
      errors.name = 'Name cannot be empty';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }
    setLoading(true);
    try {
      const payload: SubjectUpdate = {};
      if (formData.name !== undefined) payload.name = formData.name.trim();
      if (formData.short_name !== undefined) payload.short_name = (formData.short_name || '').trim();
      await axios.patch(`/api/subjects/${id}/`, payload);
      setSuccess(true);
      setTimeout(() => navigate('/subjects'), 1200);
    } catch (err: any) {
      console.error('Error updating subject:', err);
      const data = err.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const msg = Object.entries(data)
          .map(([f, m]) => `${f}: ${Array.isArray(m) ? m.join(', ') : m}`)
          .join('; ');
        setError(msg || 'Failed to update subject.');
      } else {
        setError('Failed to update subject. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this subject? Classes still using its name as a string will not be affected.')) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await axios.delete(`/api/subjects/${id}/`);
      navigate('/subjects');
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.detail) {
        setError(typeof data.detail === 'string' ? data.detail : 'Failed to delete subject.');
      } else {
        setError('Failed to delete subject. Some classes may still reference it.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/subjects')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Subjects
        </MuiLink>
        <Typography color="text.primary">Edit {subject?.name || 'Subject'}</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Edit Subject</Typography>
        {subject && (
          <Typography variant="body2" color="textSecondary">
            {subject.school_name}{subject.adam_subject_id ? ` · Adam ID ${subject.adam_subject_id}` : ''}
          </Typography>
        )}
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>Saved. Redirecting…</Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                label="Name"
                name="name"
                value={formData.name ?? ''}
                onChange={handleChange}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
                disabled={loading}
              />
              <TextField
                fullWidth
                label="Short name"
                name="short_name"
                value={formData.short_name ?? ''}
                onChange={handleChange}
                disabled={loading}
              />
            </Stack>

            <Box display="flex" gap={2} justifyContent="space-between" mt={2}>
              <Button color="error" onClick={handleDelete} disabled={loading}>
                Delete
              </Button>
              <Box display="flex" gap={2}>
                <Button variant="outlined" onClick={() => navigate('/subjects')} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Saving…' : 'Save Changes'}
                </Button>
              </Box>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default SubjectEditForm;
